import { useState } from 'react';
import AccountingPreviewTable from '../AccountingPreviewTable';
import AccountingChangeRecordsTable from '../AccountingChangeRecordsTable';

const REVERT_REASON_TEMPLATES = [
  'Fix made accounts worse — undoing to previous values.',
  'Wrong fix was applied by mistake — reverting this change.',
  'Customer reported incorrect profit after fix — rolling back.',
];

function logActionClass(action) {
  if (action === 'apply') return 'critical';
  if (action === 'revert') return 'warning';
  if (action === 'verify') return 'info';
  return 'info';
}

export default function CorrectionCaseLog({
  caseRecord,
  onClose,
  onRevert,
  reverting = false,
  showPreview = true,
}) {
  const [revertReason, setRevertReason] = useState('');
  const [showRevertForm, setShowRevertForm] = useState(false);

  if (!caseRecord) return null;

  return (
    <div className="admin-panel accounting-case-log border border-success">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
        <div>
          <h5 className="mb-1">
            Audit case #{caseRecord.id}
            {caseRecord.is_reverted && (
              <span className="admin-badge audit-severity warning ms-2">Undone</span>
            )}
          </h5>
          <p className="small mb-0">
            <strong>{caseRecord.fix_label}</strong>
            {' · '}
            {caseRecord.rows_affected} row(s) updated
            {' · '}
            {caseRecord.applied_at || caseRecord.created_at}
          </p>
          <p className="small text-muted mb-0 mt-1"><strong>Reason:</strong> {caseRecord.apply_reason}</p>
          <p className="small text-muted mb-0">
            {caseRecord.created_by_name && <span><strong>Created by:</strong> {caseRecord.created_by_name} · </span>}
            {caseRecord.applied_by_name && <span><strong>Applied by:</strong> {caseRecord.applied_by_name} · </span>}
            {(caseRecord.updated_by_name || caseRecord.reverted_by_name) && (
              <span><strong>Last updated by:</strong> {caseRecord.reverted_by_name || caseRecord.updated_by_name}</span>
            )}
          </p>
          {caseRecord.is_reverted && caseRecord.revert_reason && (
            <p className="small text-muted mb-0"><strong>Undo reason:</strong> {caseRecord.revert_reason}</p>
          )}
        </div>
        <div className="d-flex gap-1">
          {caseRecord.can_revert && !showRevertForm && (
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setShowRevertForm(true)}>
              Undo this fix
            </button>
          )}
          {onClose && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>

      {showRevertForm && caseRecord.can_revert && (
        <div className="accounting-revert-box mb-3">
          <p className="small mb-2">This will restore values to how they were before this fix. A log entry will be saved.</p>
          <div className="d-flex flex-wrap gap-1 mb-2">
            {REVERT_REASON_TEMPLATES.map((t, i) => (
              <button key={i} type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setRevertReason(t)}>
                Example {i + 1}
              </button>
            ))}
          </div>
          <textarea
            className="form-control form-control-sm mb-2"
            rows={2}
            value={revertReason}
            onChange={(e) => setRevertReason(e.target.value)}
            placeholder="Why are you undoing this fix? (min 10 characters)"
          />
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={reverting || revertReason.trim().length < 10}
              onClick={() => onRevert?.(revertReason.trim())}
            >
              {reverting ? 'Undoing…' : 'Confirm undo'}
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setShowRevertForm(false); setRevertReason(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="accounting-change-records-block mt-3 mb-3">
        <h6 className="small text-muted mb-2">Records changed (before → after)</h6>
        <AccountingChangeRecordsTable
          records={caseRecord.change_records || []}
          total={caseRecord.change_records_total || 0}
          truncated={caseRecord.change_records_truncated}
          source={caseRecord.change_records_source}
        />
      </div>

      <h6 className="small text-muted mb-2">Activity timeline</h6>
      {(caseRecord.logs || []).length === 0 ? (
        <p className="small text-muted mb-0">No log entries yet.</p>
      ) : (
        <div className="accounting-log-timeline">
          {caseRecord.logs.map((log) => (
            <div key={log.id} className="accounting-log-line">
              <span className={`admin-badge audit-severity ${logActionClass(log.action)}`}>
                {log.action_label || log.action}
              </span>
              <span className="small ms-2">{log.message}</span>
              <div className="text-muted small ms-1">
                {log.performed_by_name ? `${log.performed_by_name} · ` : ''}{log.created_at}
              </div>
              {log.action === 'apply' && log.payload?.change_records_sample?.length > 0 && (
                <details className="mt-2 ms-1">
                  <summary className="small">
                    Sample saved in log ({log.payload.change_records_sample.length}
                    {log.payload.change_records_total > log.payload.change_records_sample.length
                      ? ` of ${log.payload.change_records_total}`
                      : ''}
                    )
                  </summary>
                  <AccountingChangeRecordsTable
                    records={log.payload.change_records_sample}
                    total={log.payload.change_records_total || log.payload.change_records_sample.length}
                    truncated={log.payload.change_records_truncated}
                  />
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {showPreview && caseRecord.preview_payload && (
        <details className="mt-3">
          <summary className="small">Preview snapshot (technical)</summary>
          <AccountingPreviewTable preview={caseRecord.preview_payload} />
        </details>
      )}
    </div>
  );
}
